import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-8 flex-1">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-tax-deepBlue mb-8">Privacy Policy</h1>
          
          <p className="text-tax-neutralGray mb-8">
            <strong>Effective Date:</strong> August 15, 2025
          </p>
          
          <div className="prose prose-slate max-w-none text-tax-neutralGray">
            <p className="mb-6">
              This Privacy Policy explains how YourWayHome.com LLC ("Company," "we," "us," or "our") collects, uses, 
              shares, and protects information when you use PropertyTaxHero.com and our related services (the "Services").
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">1. Information We Collect</h2>
              <p className="mb-4">
                We may collect the following types of information when you use our Services:
              </p>
              <p className="mb-4">
                <strong>Personal Information:</strong> Name, email address, phone number, and property details you provide.
              </p>
              <p className="mb-4">
                <strong>Property Data:</strong> Information about your property, which is primarily obtained from public records.
              </p>
              <p className="mb-4">
                <strong>Technical Data:</strong> IP address, browser type, device identifiers, and usage information for 
                site performance and analytics.
              </p>
              <p className="mb-4">
                We do not collect or store confidential financial data such as Social Security numbers, bank account 
                details, or income documentation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">2. How We Use Information</h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Provide, maintain, and improve the Services.</li>
                <li className="mb-2">Generate property tax reports and appeals.</li>
                <li className="mb-2">Communicate with you about your account, reports, or updates.</li>
                <li className="mb-2">Share relevant services from our wholly owned sites, such as MortgageNews.org.</li>
                <li className="mb-2">Comply with legal obligations and enforce our Terms of Service.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">3. How We Share Information</h2>
              <p className="mb-4">
                <strong>With Wholly Owned Sites:</strong> We may share your data with our affiliated websites, 
                including MortgageNews.org.
              </p>
              <p className="mb-4">
                <strong>With Referring Agents:</strong> If you accessed PropertyTaxHero.com through a real estate agent 
                whose name and photo are shown on the website with the label "Courtesy Of," that agent will have access 
                to your name, property details, and report so they can assist you.
              </p>
              <p className="mb-4">
                <strong>Legal Compliance:</strong> We may disclose information if required by law or in response to 
                valid legal requests.
              </p>
              <p className="mb-4">
                We do not sell or rent your personal information to third parties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">4. Data Security</h2>
              <p className="mb-4">
                We implement reasonable safeguards to protect your information from unauthorized access, disclosure, 
                alteration, or destruction. However, no method of transmission over the Internet or storage system 
                is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">5. Data Retention</h2>
              <p className="mb-4">
                We retain your information only as long as necessary to provide the Services, comply with legal 
                obligations, or resolve disputes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">6. Your Choices</h2>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">You may update or delete your account information by contacting us.</li>
                <li className="mb-2">You may opt out of marketing emails by following the unsubscribe instructions in those emails.</li>
                <li className="mb-2">You may decline to provide certain information, but this may limit your ability to use some features of the Services.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">7. Children's Privacy</h2>
              <p className="mb-4">
                The Services are not directed to children under 13, and we do not knowingly collect personal information 
                from them. If we become aware that we have inadvertently collected such information, we will delete it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">8. Changes to This Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. If we make material changes, we will notify you 
                by posting an updated version on the website with a new "Effective Date."
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">9. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-tax-lightGray p-4 rounded-lg">
                <p className="mb-2"><strong>YourWayHome.com LLC</strong></p>
                <p className="mb-2">2205 W 136th Ave, Ste 106-107</p>
                <p className="mb-2">Broomfield, CO 80023</p>
                <p>Email: <a href="mailto:support@propertytaxhero.com" className="text-tax-brightBlue hover:underline">support@propertytaxhero.com</a></p>
              </div>
            </section>
          </div>

          <div className="mt-10 mb-6">
            <div className="flex space-x-4 justify-center">
              <Link to="/" className="text-tax-brightBlue hover:underline">Home</Link>
              <Link to="/about" className="text-tax-brightBlue hover:underline">About Us</Link>
              <Link to="/terms" className="text-tax-brightBlue hover:underline">Terms of Service</Link>
            </div>
          </div>

          <div className="border-t border-tax-borderGray pt-6 mt-6 text-center text-sm text-tax-neutralGray">
            <p>&copy; {new Date().getFullYear()} PropertyTaxHero.Com. All rights reserved.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;